import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import {
  AdminDataService,
  InternalAdminVideoItem,
  InternalAdminVideosResponse,
} from '../../controller/admin-data.service';
import { RelaunchPipelineResponse, VideoLabService } from '../../../video/controller/video-lab.service';

@Component({
  selector: 'app-internal-video-management',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSnackBarModule],
  templateUrl: './internal-video-management.component.html',
  styleUrls: ['./internal-video-management.component.scss']
})
export class InternalVideoManagementComponent implements OnInit {
  public readonly pageSize = 10;

  public videos: InternalAdminVideoItem[] = [];
  public searchQuery = '';
  public isLoading = false;
  public feedbackMessage = '';
  public currentPage = 1;
  public total = 0;
  public hasNextPage = false;
  public hasPreviousPage = false;

  private relaunchingVideoIds = new Set<string>();

  constructor(
    private adminDataService: AdminDataService,
    private videoLabService: VideoLabService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.loadVideos();
  }

  public loadVideos(page: number = this.currentPage): void {
    this.isLoading = true;
    this.feedbackMessage = '';

    this.adminDataService.getInternalAdminVideos(page, this.pageSize, this.searchQuery.trim()).subscribe({
      next: (response: InternalAdminVideosResponse) => {
        this.applyResponse(response);
        this.isLoading = false;
      },
      error: () => {
        this.feedbackMessage = 'No se pudo cargar la lista de vídeos.';
        this.videos = [];
        this.isLoading = false;
      }
    });
  }

  public searchVideos(): void {
    this.currentPage = 1;
    this.loadVideos(1);
  }

  public goToPreviousPage(): void {
    if (!this.hasPreviousPage || this.isLoading) {
      return;
    }

    this.loadVideos(this.currentPage - 1);
  }

  public goToNextPage(): void {
    if (!this.hasNextPage || this.isLoading) {
      return;
    }

    this.loadVideos(this.currentPage + 1);
  }

  public onRelaunchVideo(video: InternalAdminVideoItem): void {
    if (!video.video_id || this.isRelaunching(video.video_id)) {
      return;
    }

    if (!confirm(`¿Relanzar la IA del vídeo "${video.title}"?`)) {
      return;
    }

    this.relaunchingVideoIds.add(video.video_id);
    this.executeRelaunch(video.video_id, false);
  }

  public isRelaunching(videoId: string): boolean {
    return this.relaunchingVideoIds.has(videoId);
  }

  public get pageLabel(): string {
    if (!this.total) {
      return 'Sin resultados';
    }

    const from = (this.currentPage - 1) * this.pageSize + 1;
    const to = Math.min(this.currentPage * this.pageSize, this.total);
    return `${from}-${to} de ${this.total}`;
  }

  private applyResponse(response: InternalAdminVideosResponse): void {
    this.videos = response.items;
    this.total = response.total;
    this.currentPage = response.page;
    this.hasNextPage = response.has_next_page;
    this.hasPreviousPage = response.has_previous_page;
  }

  private executeRelaunch(videoId: string, forceRestart: boolean): void {
    this.videoLabService.relaunchPipeline(videoId, forceRestart).subscribe({
      next: (response: RelaunchPipelineResponse) => {
        if (response.status === 'needs_confirmation' && !forceRestart) {
          this.relaunchingVideoIds.delete(videoId);
          const shouldForce = confirm(
            'El vídeo está actualmente en PROCESSING en el orquestador.\n¿Deseas pararlo y relanzarlo ahora?'
          );
          if (!shouldForce) {
            return;
          }

          this.relaunchingVideoIds.add(videoId);
          this.executeRelaunch(videoId, true);
          return;
        }

        const isSkipped = response.status === 'skipped';
        this.snackBar.open(response.message, 'OK', {
          duration: 5000,
          panelClass: isSkipped ? ['warning-snackbar'] : ['success-snackbar']
        });
        this.relaunchingVideoIds.delete(videoId);
      },
      error: (error) => {
        const errorMsg = error.error?.detail || 'Error desconocido al contactar con el servidor';
        this.snackBar.open(`Error: ${errorMsg}`, 'Cerrar', { duration: 5000 });
        this.relaunchingVideoIds.delete(videoId);
      }
    });
  }
}
